import React from 'react';
import { motion } from 'framer-motion';
import { 
  Funnel, 
  Cog, 
  Target, 
  TrendingUp,
  ArrowRight
} from 'lucide-react';

const services = [
  {
    icon: Funnel,
    title: 'Funnel Design',
    description: 'Custom marketing funnels architected for your specific business goals and audience.',
    features: ['Customer journey mapping', 'Page design & copywriting', 'A/B testing framework'],
  },
  {
    icon: Cog,
    title: 'Marketing Automation',
    description: 'Automated workflows that nurture leads and drive conversions while you sleep.',
    features: ['Email sequences', 'CRM integration', 'Workflow optimization'],
  },
  {
    icon: Target,
    title: 'Lead Generation Systems',
    description: 'Multi-channel lead capture strategies that fill your pipeline with qualified prospects.',
    features: ['Landing page creation', 'Lead magnet development', 'Traffic strategy'],
  },
  {
    icon: TrendingUp,
    title: 'Conversion Optimization',
    description: 'Data-driven improvements that maximize the ROI of your existing marketing.',
    features: ['Funnel analytics', 'Conversion audits', 'Ongoing optimization'],
  },
];

export default function ServicesSection() {
  const scrollToContact = () => {
    const element = document.querySelector('#contact');
    if (element) element.scrollIntoView({ behavior: 'smooth' });
  };
  
  return (
    <section 
      id="services"
      data-testid="services-section"
      className="section-padding relative overflow-hidden"
    >
      <div className="absolute inset-0 bg-[#030303]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom,_rgba(0,240,255,0.05)_0%,_transparent_70%)]" />
      
      <div className="container-custom relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="text-xs uppercase tracking-[0.2em] text-cyan-400 mb-4 block">
            What We Offer
          </span>
          <h2 
            data-testid="services-headline"
            className="text-4xl md:text-5xl font-bold text-white mb-6"
          >
            Marketing
            <br />
            <span className="gradient-text-cyan">Services</span>
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-base">
            End-to-end solutions designed to build, optimize, and scale your 
            marketing funnel system.
          </p>
        </motion.div>
        
        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
          {services.map((service, index) => (
            <motion.div
              key={index}
              data-testid={`service-card-${index}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1, duration: 0.5 }}
              className="group"
            >
              <div className="glass rounded-2xl p-6 h-full card-hover border border-white/10">
                {/* Icon */}
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-5 bg-white/5 group-hover:bg-white/10 transition-colors">
                  <service.icon className="w-6 h-6 text-white" />
                </div>
                
                {/* Content */}
                <h3 className="text-lg font-semibold text-white mb-2">
                  {service.title}
                </h3>
                <p className="text-gray-400 text-sm leading-relaxed mb-4">
                  {service.description}
                </p>
                
                {/* Features */}
                <ul className="space-y-2 mb-4">
                  {service.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-gray-400">
                      <div className="w-1.5 h-1.5 rounded-full bg-gray-600" />
                      {feature}
                    </li>
                  ))}
                </ul>
                
                {/* CTA */}
                <button
                  onClick={scrollToContact}
                  className="flex items-center gap-2 text-sm font-medium text-gray-400 hover:text-white transition-colors"
                >
                  <span>Learn More</span>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
